import { Box, Button, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Holiday, HolidayState, updateSchambeckUserTableConfig, useData } from 'shared';
import { Right } from 'shared/src/userRole/UserRole.types';
import { NotAllowed } from '../../components/NotAllowed';
import { useUser } from '../../components/UserContext';
import { convertHolidayRequestsToTableEntries } from '../../utils/holiday/Holiday.utils';
import { isUserAllowedToDo } from '../../utils/user/User.utils';

const HolidayRequestOverview: React.FC = () => {
  const { user, axios, updateContextUser, selectedOffices } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filterValue, setFilterValue] = useState<string>('');
  const [filterType, setFilterType] = useState<HolidayState>(HolidayState.PENDING);
  const { data: loadedHolidayRequests } = useData('HOLIDAYS_ALL_SUBS', {
    params: { userId: user.id },
    config: { fallbackData: [] },
  });
  const { data: loadedUsers } = useData('SCHAMBECK_USERS_ALL', {
    params: { userId: user.id },
    config: { fallbackData: [] },
  });

  const { data: loadedReasons } = useData('ABSENCE_REASONS_ALL', {
    config: { fallbackData: [] },
  });

  //Holds tableRows for holidayRequests
  const rows: TableRow[] = useMemo((): TableRow[] => {
    const requestsToDisplay: Holiday[] = loadedHolidayRequests.filter((request) => {
      let stateShouldBeDisplayed: boolean = false;
      switch (filterType) {
        case HolidayState.PENDING:
          stateShouldBeDisplayed = !request.approved && !request.denied;
          break;
        case HolidayState.REJECTED:
          stateShouldBeDisplayed = request.denied;
          break;
        case HolidayState.APPROVED:
          stateShouldBeDisplayed = request.approved;
          break;
      }
      const userOffice: string | undefined = loadedUsers.find((user) => user.id === request.userId)
        ?.contractInformation.currentOfficeId;
      const userShouldBeDisplayed: boolean = !!userOffice && selectedOffices.includes(userOffice);
      return userShouldBeDisplayed && stateShouldBeDisplayed;
    });
    return convertHolidayRequestsToTableEntries(
      requestsToDisplay,
      loadedUsers,
      navigate,
      loadedReasons,
      user.language,
    );
  }, [loadedHolidayRequests, loadedReasons, loadedUsers, navigate, filterType, user.language]);

  return isUserAllowedToDo(user.right, Right.HOLIDAY_EDIT) ? (
    <div className="holiday-requests__overview__wrapper">
      <TopBar
        onSearch={setFilterValue}
        title={t('pages.holiday.createOrEdit.headline')}
        onBackClick={() => navigate(-1)}
      />
      <Box>
        <div className="three-columns">
          {Object.values(HolidayState).map((state) => (
            <Button
              value={t(`general.holidayState.${state}`)}
              active={filterType === state}
              onClick={() => setFilterType(state)}
            />
          ))}
        </div>
        <Table
          tableConfig={user.tableConfig?.get(TableKey.HOLIDAY_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.BRANCHE_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.holiday.requestOverview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </div>
  ) : (
    <NotAllowed neccessaryRight={Right.HOLIDAY_EDIT} />
  );
};
export default HolidayRequestOverview;
